import { useEffect, useRef } from "react";

/**
 * xmg-actions-first: the body of every stage screen. What needs the operator comes first (`questions`: the open
 * BLOCK questions with their answer controls), the primary action sits beside it and stays in view (`action`),
 * and everything else (`children`: collapsed sections of data) comes below.
 *
 * Wide screens: questions and details in the left column, the action card sticky in the right one. Narrow
 * screens: one column, the action reads as a bar pinned to the bottom edge while the page scrolls, whatever the
 * length of the questions above it. The DOM order is questions, action, details, so keyboard and screen-reader
 * order follow the importance too.
 */
export function ActionsFirst({
  questions,
  action,
  actionLabel,
  pinned = false,
  children,
}: {
  questions?: React.ReactNode;
  action: React.ReactNode;
  /** Names the action region for assistive technology. */
  actionLabel: string;
  /**
   * A sign-off or approve action is open: the card stays in view (sticky beside the questions, a bar on a
   * phone). A card that only reports (step outcome, read-only, failed) is a plain, static card.
   */
  pinned?: boolean;
  children?: React.ReactNode;
}): JSX.Element {
  // Below the wide breakpoint a pinned action is a fixed bar; the page keeps its height free (padding and
  // scroll padding) so it never covers the last row or a focused control.
  const actionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const element = actionRef.current;
    if (!pinned || element === null || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const apply = (): void => {
      const fixed = getComputedStyle(element).position === "fixed";
      if (fixed) root.style.setProperty("--afbar-h", `${element.offsetHeight}px`);
      else root.style.removeProperty("--afbar-h");
    };
    apply();
    // `focus()` on an element that is already inside the viewport scrolls nothing, so an element in the strip the
    // bar covers would take focus unseen (and scroll-padding does not apply): bring it above the bar.
    const onFocusIn = (event: FocusEvent): void => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || element.contains(target) || target.closest("[role='dialog']"))
        return;
      if (getComputedStyle(element).position !== "fixed") return;
      const barTop = element.getBoundingClientRect().top;
      const rect = target.getBoundingClientRect();
      if (rect.bottom > barTop) window.scrollBy({ top: rect.bottom - barTop + 16 });
    };
    document.addEventListener("focusin", onFocusIn);
    const observer = new ResizeObserver(apply);
    observer.observe(element);
    window.addEventListener("resize", apply);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      observer.disconnect();
      window.removeEventListener("resize", apply);
      root.style.removeProperty("--afbar-h");
    };
  }, [pinned]);
  const hasQuestions = questions !== undefined && questions !== null && questions !== false;
  return (
    <div
      className={["afgrid", hasQuestions ? "" : "afgrid--noquestions", pinned ? "afgrid--pinned" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      {hasQuestions ? <div className="afgrid__questions">{questions}</div> : null}
      <aside className="afgrid__action" aria-label={actionLabel} ref={actionRef}>
        {action}
      </aside>
      <div className="afgrid__details">{children}</div>
    </div>
  );
}
